/**
 * Corelewear theme helpers.
 * Small, dependency-free custom elements used by the corelewear-* sections.
 */

// Every corelewear-* section adds this script, so it can run several times per page.
// Run the body once; later runs exit early instead of redeclaring the classes below.
(() => {
if (window.__clwThemeJs) return;
window.__clwThemeJs = true;

/**
 * <corelewear-carousel> — horizontal scroll-snap track with prev/next buttons.
 * Markup: [data-carousel-track] (the scroller), [data-prev], [data-next].
 */
class CorelewearCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    if (!this.track) return;

    this.onScroll = this.updateButtons.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.track);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  scrollByPage(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
  }

  updateButtons() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflow = scrollWidth - clientWidth > 2;
    this.toggleAttribute('data-overflow', overflow);
    if (this.prevButton) this.prevButton.disabled = scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}

/**
 * <corelewear-slideshow> — fading hero slideshow with dots and optional autoplay.
 * Attributes: data-autoplay (seconds, 0 = off).
 */
class CorelewearSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.index = 0;
    if (this.slides.length < 2) return;

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

    // Basic swipe support for phones.
    let startX = null;
    this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
    this.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const delta = e.changedTouches[0].clientX - startX;
      if (Math.abs(delta) > 40) this.go(this.index + (delta < 0 ? 1 : -1), true);
      startX = null;
    });

    this.delay = Number(this.dataset.autoplay || 0) * 1000;
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.start();

    // Theme editor: jump to the selected slide.
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.findIndex((slide) => slide.contains(e.target));
      if (i > -1) {
        this.stop();
        this.go(i);
      }
    });
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    if (this.delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
  }

  stop() {
    clearInterval(this.timer);
  }

  go(i, userInitiated = false) {
    const count = this.slides.length;
    this.index = (i + count) % count;
    this.slides.forEach((slide, n) => {
      const active = n === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    this.dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === this.index)));
    if (userInitiated) this.start();
  }
}

/**
 * <corelewear-video-card> — muted looping product video with play/pause + sound toggle.
 * Videos only play while visible to save bandwidth on mobile.
 */
class CorelewearVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.muted = true;
    this.video.playsInline = true;

    this.querySelector('[data-toggle-play]')?.addEventListener('click', () => {
      this.userPaused = !this.video.paused;
      this.video.paused ? this.video.play() : this.video.pause();
    });
    this.querySelector('[data-toggle-sound]')?.addEventListener('click', () => {
      this.video.muted = !this.video.muted;
      this.toggleAttribute('data-sound-on', !this.video.muted);
    });
    this.video.addEventListener('play', () => this.setAttribute('data-playing', ''));
    this.video.addEventListener('pause', () => this.removeAttribute('data-playing'));

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.userPaused) this.video.play().catch(() => {});
        else this.video.pause();
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);

    /* Posters are set from data-poster shortly before the card scrolls into view,
       so below-the-fold posters do not compete with the product image on load. */
    const poster = this.video.dataset.poster;
    if (poster && !this.video.poster) {
      this.posterObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.video.poster = poster;
          this.posterObserver.disconnect();
        },
        { rootMargin: '800px 0px' }
      );
      this.posterObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.posterObserver?.disconnect();
  }
}

/** <corelewear-scroll-top> — button that returns to the top of the page. */
class CorelewearScrollTop extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    });
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
define('corelewear-carousel', CorelewearCarousel);
define('corelewear-slideshow', CorelewearSlideshow);
define('corelewear-video-card', CorelewearVideoCard);
define('corelewear-scroll-top', CorelewearScrollTop);

/**
 * Microsoft Clarity enrichment (the Clarity app embed loads the tag itself).
 * Calls are queued with Clarity's standard stub, so they are safe before the
 * tag loads and do nothing if Clarity is disabled. No personal data is sent.
 * - Tags: page_type, product, traffic, utm_source/campaign/content, size_selected
 * - Events: every data-track click, add_to_cart, variant_change, scroll_75
 * - Sessions with add_to_cart / checkout_now are upgraded (kept in full).
 */
(() => {
  if (window.clwClarity) return;

  const clarity = (...args) => {
    try {
      window.clarity =
        window.clarity ||
        function () {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };
      window.clarity(...args);
    } catch (e) {}
  };
  const tag = (key, value) => {
    if (value) clarity('set', key, String(value).slice(0, 100));
  };
  const event = (name) => clarity('event', name);
  const upgrade = (reason) => clarity('upgrade', reason);

  /* Page context */
  const pageType = window.ShopifyAnalytics?.meta?.page?.pageType || (location.pathname === '/' ? 'home' : '');
  tag('page_type', pageType);
  const productMatch = location.pathname.match(/\/products\/([^/?#]+)/);
  if (productMatch) tag('product', decodeURIComponent(productMatch[1]));

  /* Traffic source: read on the landing page, remembered for the session */
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  let source = {};
  try {
    source = JSON.parse(sessionStorage.getItem('clw_source') || '{}');
  } catch (e) {}
  const params = new URLSearchParams(location.search);
  if (UTM_KEYS.some((key) => params.has(key)) || params.has('fbclid')) {
    source = {};
    UTM_KEYS.forEach((key) => {
      if (params.get(key)) source[key] = params.get(key);
    });
    const metaSource = /facebook|instagram|^fb$|^ig$|meta/i.test(source.utm_source || '');
    source.traffic = params.has('fbclid') || metaSource ? 'meta_ads' : 'paid_other';
    try {
      sessionStorage.setItem('clw_source', JSON.stringify(source));
    } catch (e) {}
  }
  tag('traffic', source.traffic || (document.referrer ? 'referral_or_organic' : 'direct'));
  ['utm_source', 'utm_campaign', 'utm_content'].forEach((key) => tag(key, source[key]));

  /* Add to cart (product form submit, incl. sticky bar) */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (form instanceof HTMLFormElement && /\/cart\/add/.test(form.getAttribute('action') || '')) {
        event('add_to_cart');
        upgrade('add_to_cart');
      }
    },
    { capture: true }
  );

  /* Variant change: color / size picks */
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!(input instanceof Element) || !input.closest('variant-picker')) return;
    let name = '';
    let value = '';
    if (input instanceof HTMLSelectElement) {
      const option = input.selectedOptions[0];
      name = option?.dataset.optionName || '';
      value = option?.value || '';
    } else if (input instanceof HTMLInputElement) {
      name = input.dataset.optionName || '';
      value = input.value;
    }
    event('variant_change');
    if (/size/i.test(name)) tag('size_selected', value);
    else if (/colou?r/i.test(name)) tag('color_selected', value);
  });

  /* Scroll depth on product pages */
  if (productMatch) {
    const onScroll = () => {
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth < 0.75) return;
      event('scroll_75');
      window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  window.clwClarity = {
    click(payload) {
      if (!payload?.event) return;
      event(payload.event);
      if (payload.event === 'checkout_now') {
        event('add_to_cart');
        upgrade('checkout_now');
      }
    },
  };
})();

/**
 * Click tracking for elements with data-track.
 * data-track = event name (e.g. "hero_cta", "need_tile", "size_guide_open", "card_add")
 * data-track-label = human readable detail (e.g. the pain point text)
 * Sends to Shopify Customer Events (custom pixel can subscribe to "clw_click")
 * and to window.dataLayer if present (GTM).
 */
(() => {
  if (window.__clwTrackBound) return;
  window.__clwTrackBound = true;

  const send = (el) => {
    const payload = {
      event: el.dataset.track,
      label: el.dataset.trackLabel || el.textContent.trim().slice(0, 80),
      section: el.closest('.shopify-section')?.id || '',
      href: el.getAttribute('href') || '',
    };
    try {
      window.Shopify?.analytics?.publish?.('clw_click', payload);
    } catch (e) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'clw_click', ...payload });
    window.clwClarity?.click(payload);
  };

  document.addEventListener(
    'click',
    (event) => {
      const el = event.target instanceof Element ? event.target.closest('[data-track]:not([data-track=""])') : null;
      if (el) send(el);
    },
    { capture: true }
  );

  /* "Find My Size" triggers: any [data-open-size-guide] opens the shared size guide dialog. */
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-open-size-guide]') : null;
    if (!trigger) return;
    const dialog = document.getElementById('clw-size-guide');
    if (dialog && typeof dialog.showModal === 'function') {
      event.preventDefault();
      dialog.showModal();
    }
  });
})();
})();
